/**
 * Registro do Service Worker para PWA
 * 
 * Este módulo é responsável por:
 * 1. Registrar o service worker em produção
 * 2. Detectar quando uma nova versão do service worker está disponível
 * 3. Notificar o usuário sobre atualizações
 */

// Verificar se o navegador suporta service worker
const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

// Função para registrar o service worker
export function register(config) {
  // Apenas registrar em produção e se o browser suportar service worker
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    
    // Não registrar se a origem do service worker não é a mesma do app
    if (publicUrl.origin !== window.location.origin) {
      return;
    }

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      if (isLocalhost) {
        // Estamos rodando em localhost, verificar se o service worker ainda existe
        checkValidServiceWorker(swUrl, config);

        navigator.serviceWorker.ready.then(() => {
          console.log('Este aplicativo está sendo servido em cache pelo service worker.');
        });
      } else {
        // Não é localhost, registrar o service worker diretamente
        registerValidSW(swUrl, config);
      }
    });
  }
}

// Função para registrar um service worker válido
function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then(registration => {
      // Detectar quando uma nova versão do service worker está disponível
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }

        // Monitora mudanças de estado
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // Service worker atualizado disponível
              console.log('Nova versão disponível! O aplicativo será atualizado após recarregar.');

              // Notificar o usuário se temos uma função de callback configurada
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              // Primeiro service worker instalado
              console.log('Conteúdo em cache para uso offline.');

              // Notificar o usuário que o conteúdo está pronto para offline
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };
    })
    .catch(error => {
      console.error('Erro durante o registro do service worker:', error);
    });
}

// Verificar se o service worker é válido 
function checkValidServiceWorker(swUrl, config) {
  // Verificar se podemos encontrar o service worker
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then(response => {
      // Certificar-se de que o service worker existe e a resposta é válida
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        // Nenhum service worker encontrado. Provavelmente uma app diferente
        navigator.serviceWorker.ready.then(registration => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        // Service worker encontrado. Registrá-lo normalmente
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('Sem conexão com a internet. App rodando em modo offline.');
    });
}

// Função para cancelar o registro do service worker (útil para limpeza)
export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then(registration => {
        registration.unregister();
      })
      .catch(error => {
        console.error(error.message);
      });
  }
}

// Função para atualizar o service worker imediatamente (útil para atualizações críticas)
export function updateServiceWorker() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready.then(registration => {
      registration.update();
    });
  }
}

// Função para forçar a atualização do service worker e recarregar a página (atualizações importantes)
export function forceUpdate() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready.then(registration => {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      window.location.reload();
    });
  }
} 